import Animated from 'react-native-reanimated';
import i18n from '@/utils/i18n';
import React, { useState } from 'react';
import {
  ActionSheet,
  List,
  Separator,
  shadowStyle,
  Spacer,
  YStack,
} from '@usepepper/sprout-kit-ui';
import {
  Alert,
  AlertCardType,
} from '@/experiences/sales-portal/domain/insights/blocs/types/alert';
import { AlertCard } from '@/experiences/sales-portal/domain/insights/components/alert-card/alert-card';
import { AlertCardSurface } from '@/experiences/sales-portal/domain/insights/components/alert-card/alert-card-surface';
import { CategoryTogglePill } from '@/experiences/sales-portal/domain/insights/components/category-toggle-pill';
import { INSIGHTS_MOBILE_LAYOUT_TRANSITION } from '@/experiences/sales-portal/domain/insights/components/constants';
import { Platform, StyleSheet, View } from 'react-native';
import { useAlertPress } from '@/experiences/sales-portal/domain/insights/blocs/hooks/use-alert-press';
import { useTheme } from '@/contexts/theme-context';
import type { AlertCategory } from '@/experiences/sales-portal/domain/insights/blocs/transformers/alert-transformer';

const COLLAPSED_VISIBLE_COUNT = 1;
const MENU_SNAP_POINTS = [25];
const STACK_PEEK = 8;
const STACK_INSET = 8;
const STACK_LAYER_HEIGHT = 24;
const STACK_SHADOW_TIER = 'soft0';
const IS_ANDROID = Platform.OS === 'android';
const STACK_SHADOW_ELEVATION = IS_ANDROID ? 0 : 1;

const stackLayerStyles = StyleSheet.create({
  base: {
    borderRadius: 12,
    height: STACK_LAYER_HEIGHT,
    position: 'absolute',
    ...(IS_ANDROID ? { borderWidth: 1 } : null),
  },
  far: {
    bottom: -STACK_PEEK * 2,
    left: STACK_INSET * 2,
    right: STACK_INSET * 2,
  },
  near: {
    bottom: -STACK_PEEK,
    left: STACK_INSET,
    right: STACK_INSET,
    ...(IS_ANDROID
      ? null
      : shadowStyle(STACK_SHADOW_TIER, STACK_SHADOW_ELEVATION)),
  },
});

export interface InsightsMobileAlertFeedProps {
  categories: AlertCategory[];
  onDelete: (alert: Alert) => void;
  onHide: (alert: Alert) => void;
  supplierUUID: string;
}

export const InsightsMobileAlertFeed: React.FC<
  InsightsMobileAlertFeedProps
> = ({ categories, onDelete, onHide, supplierUUID }) => {
  const theme = useTheme();
  const { onAccountDetailPress, onAlertPress } = useAlertPress(supplierUUID);
  const layerBorderColor = IS_ANDROID
    ? { borderColor: theme.sk.color.border.primary }
    : null;
  const farLayerColors = [
    { backgroundColor: theme.sk.color.bg.tertiary.default },
    layerBorderColor,
  ];
  const nearLayerColors = [
    { backgroundColor: theme.sk.color.bg.secondary.default },
    layerBorderColor,
  ];
  const [expandedCategories, setExpandedCategories] = useState<
    Set<AlertCardType>
  >(new Set());
  const [menuAlert, setMenuAlert] = useState<Alert | null>(null);

  const getAlertActions = (alert: Alert) => [
    {
      key: 'hide',
      label: i18n.t('insights_hub.action_sheet.hide'),
      onPress: () => onHide(alert),
    },
    {
      key: 'delete',
      label: i18n.t('delete'),
      onPress: () => onDelete(alert),
    },
  ];

  const toggleCategory = (cardType: AlertCardType) => {
    setExpandedCategories((previous) => {
      const next = new Set(previous);
      if (next.has(cardType)) {
        next.delete(cardType);
      } else {
        next.add(cardType);
      }
      return next;
    });
  };

  const categoryRows = categories.map((category) => {
    const isExpanded = expandedCategories.has(category.cardType);
    return {
      cardType: category.cardType,
      isExpanded,
      isTruncated: category.alerts.length > COLLAPSED_VISIBLE_COUNT,
      totalCount: category.alerts.length,
      visibleAlerts: isExpanded
        ? category.alerts
        : category.alerts.slice(0, COLLAPSED_VISIBLE_COUNT),
    };
  });

  return (
    <YStack gap="$8">
      {categoryRows.map((category) => {
        const isStacked = category.isTruncated && !category.isExpanded;
        return (
          <Animated.View
            key={category.cardType}
            layout={INSIGHTS_MOBILE_LAYOUT_TRANSITION}
          >
            <YStack marginBottom={isStacked ? STACK_PEEK * 2 : 0}>
              {isStacked ? (
                <>
                  <View
                    style={[
                      stackLayerStyles.base,
                      stackLayerStyles.far,
                      farLayerColors,
                    ]}
                  />
                  <View
                    style={[
                      stackLayerStyles.base,
                      stackLayerStyles.near,
                      nearLayerColors,
                    ]}
                  />
                </>
              ) : null}
              <Animated.View layout={INSIGHTS_MOBILE_LAYOUT_TRANSITION}>
                <AlertCardSurface>
                  {category.visibleAlerts.map((alert, alertIndex) => {
                    const isLastAlert =
                      alertIndex === category.visibleAlerts.length - 1;
                    return (
                      <React.Fragment key={alert.alertUUID}>
                        <YStack paddingBottom={isLastAlert ? 0 : '$16'}>
                          <AlertCard
                            actions={getAlertActions(alert)}
                            alert={alert}
                            onAlertCardPress={onAccountDetailPress(alert)}
                            onContentPress={onAlertPress(alert)}
                            onMenuPress={() => setMenuAlert(alert)}
                          />
                        </YStack>
                        {isLastAlert ? null : <Separator />}
                      </React.Fragment>
                    );
                  })}
                  {isStacked ? (
                    <YStack alignItems="center" paddingVertical="$8">
                      <CategoryTogglePill
                        count={category.totalCount}
                        expanded={false}
                        onPress={() => toggleCategory(category.cardType)}
                      />
                    </YStack>
                  ) : (
                    <Spacer size="$16" />
                  )}
                </AlertCardSurface>
              </Animated.View>
            </YStack>
            {category.isTruncated && category.isExpanded ? (
              <YStack alignItems="center" marginVertical="$8">
                <CategoryTogglePill
                  count={category.totalCount}
                  expanded
                  onPress={() => toggleCategory(category.cardType)}
                />
              </YStack>
            ) : null}
          </Animated.View>
        );
      })}
      <ActionSheet
        onOpenChange={(open) => !open && setMenuAlert(null)}
        open={menuAlert !== null}
        snapPoints={MENU_SNAP_POINTS}
      >
        <ActionSheet.Body>
          <List>
            {(menuAlert ? getAlertActions(menuAlert) : []).map(
              (action, index) => (
                <React.Fragment key={action.key}>
                  {index > 0 ? <List.Separator /> : null}
                  <List.Item
                    onPress={() => {
                      setMenuAlert(null);
                      action.onPress();
                    }}
                    paddingVertical={theme.sk.spacing.spaceSMedium}
                    pressable
                  >
                    <List.Item.Content>
                      <List.Item.Title>{action.label}</List.Item.Title>
                    </List.Item.Content>
                  </List.Item>
                </React.Fragment>
              ),
            )}
          </List>
        </ActionSheet.Body>
      </ActionSheet>
    </YStack>
  );
};
